import { CreateInitialParamsData, createInitialParams } from '@api/yembo/initialParams/_flows/createInitialParams';
import { sendSelfSurveyInvite } from '@api/yembo/initialParams/_flows/sendSelfSurveyInvite';
import { SendInviteError } from '@api/yembo/invite';
import { useNavigate } from '@tanstack/react-router';
import { Button, Drawer, Modal, ModalBody, ModalFooter, ModalHeader, notify } from '@yembo/yemblocks-core';
import { didObjectChange, toE164PhoneNumber } from '@yembo/yemblocks-core/dist/_util';
import dayjs from 'dayjs';
import { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { postNoteApi } from 'src/api/yembo/activityFeed';
import ErrorIcon from 'src/assets/svg/error.svg?react';
import { usePutEmployee } from 'src/queries/Employee';
import { getCustomizations } from 'src/queries/Company/customizations';
import { putInsuredCheckDuplicates } from 'src/queries/Insured';
import { reducer } from 'src/react/_routes/_util/hooksUtils';
import { AuthUserContext } from 'src/react/_shared/Context/AuthUserContext';
import { AddressFields } from 'src/react/_util/addressPlace';
import { AddContactCompany, BasicEmployee, CompanyDefaultSurveyType, Insured } from 'src/types';
import { formatCanadaPostalCode } from '../_routes/Job/_utils/formatCanadaPostalCode';
import { AddContactContext } from '../_shared/Context/AddContact/context';
import { DrawerBody } from './_components/DrawerBody';
import { SubmitMessage } from './_components/SubmitMessage';
import { createSurveyFlow } from './_tools/createSurveyFlow';

export type JobLocation = {
  streetAddress: null | string;
  addressLine2: null | string;
  gateCode: null | string;
  city: null | string;
  state: null | string;
  postalCode: null | string;
  beds: null | number;
  country: null | string;
};

export type ContactComponentState = {
  isFormValid: boolean;
  job: null | { key: string; expectedSurveyDate?: string | null };
  insured: null | Insured;
  isAddingContact: boolean;
  isAddingNewContact: boolean;
  isResendingInvite: boolean;
  isSendInviteError: boolean;
  sendInviteError: string;
  isSuperadmin: boolean;
  surveyType: CompanyDefaultSurveyType;
  notes: string;
};

export type DefaultInsured = {
  id: null | string;
  givenName: null | string;
  familyName: null | string;
  phone: string;
  email: null | string;
};

export type CreateContactFormData = {
  company: AddContactCompany;
  employee?: BasicEmployee;
  origin: AddressFields;
  destination: AddressFields;
  insured: DefaultInsured;
  validation: {
    isEmailValid: boolean;
    isNumberOfBedsValid: boolean;
    isCompanySelected: boolean;
  };
  shouldSendEmail: boolean;
  shouldSendSms: boolean;
};

export type ContactJobState = {
  key: null | string;
  expectedSurveyDate: null | string;
  date: null | string;
  dateKnown: boolean;
  status: 'created manually' | 'invited';
  source: 'direct' | 'invite';
  isManual: boolean;
  businessUnit: string | null;
  agentCode: string | null;
  locale: string;
};

export const Contact = () => {
  const { closeAddContact } = useContext(AddContactContext);

  const { authUser, activeRole, defaultUserCompany, firstUserCompany } = useContext(AuthUserContext);
  const { mutateAsync: putEmployee } = usePutEmployee();

  const isSuperadmin = activeRole === 'superadmin';

  const authUserCompanyKeys = authUser.impersonatedCompanyKeys?.length
    ? authUser.impersonatedCompanyKeys
    : authUser.companyKeys;

  const navigate = useNavigate();

  const { initialInsured, initialCompany: contextInitialCompany, currentPageCompany } = useContext(AddContactContext);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [shouldShowTryAgainButton, setShouldShowTryAgainButton] = useState(false);

  const getInitialCompany = () => {
    if (contextInitialCompany) return contextInitialCompany;

    const canUseCurrentPageCompany =
      !!currentPageCompany && (isSuperadmin || authUserCompanyKeys?.includes(currentPageCompany.key));

    if (canUseCurrentPageCompany) return currentPageCompany;

    return defaultUserCompany ?? firstUserCompany;
  };

  const initialCompany = getInitialCompany();

  const initialContactFormData: CreateContactFormData = useMemo(
    () => ({
      company: initialCompany,
      employee: authUser,
      insured: {
        id: null,
        givenName: '',
        familyName: '',
        phone: '',
        email: '',
      },
      validation: {
        isEmailValid: true,
        isNumberOfBedsValid: true,
        isCompanySelected: true,
      },
      shouldSendEmail: false,
      shouldSendSms: false,
      origin: {
        streetAddress: '',
        addressLine2: '',
        gateCode: '',
        city: '',
        state: '',
        postalCode: '',
        country: initialCompany.defaultCountry,
        beds: null,
      },
      destination: {
        streetAddress: '',
        addressLine2: '',
        gateCode: '',
        city: '',
        state: '',
        postalCode: '',
        country: initialCompany.defaultCountry,
        beds: null,
      },
    }),
    [authUser, initialCompany]
  );

  const [contactFormData, dispatchContactFormData] = useReducer(reducer<CreateContactFormData>, initialContactFormData);
  const { company: selectedCompany, shouldSendEmail, shouldSendSms, validation } = contactFormData;

  const initialContactState: ContactComponentState = {
    isFormValid: true,
    job: null,
    isAddingContact: false,
    isResendingInvite: false,
    isSendInviteError: false,
    sendInviteError: '',
    isAddingNewContact: false,
    insured: null,
    isSuperadmin,
    surveyType: selectedCompany.defaultSurveyType,
    notes: '',
  };

  const initialJobState: ContactJobState = {
    key: null,
    expectedSurveyDate: null,
    date: null,
    dateKnown: false,
    status: 'created manually',
    source: 'direct',
    isManual: false,
    businessUnit: null,
    agentCode: selectedCompany.agentCode,
    locale: initialCompany.defaultLocale,
  };

  const [state, dispatchState] = useReducer(reducer<ContactComponentState>, initialContactState);
  const [jobFormState, dispatchJobFormState] = useReducer(reducer<ContactJobState>, initialJobState);

  const [handleAnimationEnd, setHandleAnimationEnd] = useState<(() => void) | undefined>();

  const {
    isFormValid,
    job,
    insured,
    isAddingContact,
    isResendingInvite,
    sendInviteError,
    surveyType,
    isSendInviteError,
    isAddingNewContact,
  } = state;
  const isInvite = state.surveyType === 'self_survey' && (shouldSendSms || shouldSendEmail);

  useEffect(() => {
    if (!initialInsured) return;

    dispatchContactFormData({
      insured: {
        id: null,
        givenName: null,
        familyName: null,
        phone: '',
        email: null,
        ...initialInsured,
      },
    });
  }, [initialInsured]);

  useEffect(() => {
    dispatchContactFormData({ employee: authUser });
    dispatchState({ surveyType: selectedCompany.defaultSurveyType });
    dispatchJobFormState({ agentCode: selectedCompany.agentCode });
  }, [selectedCompany, authUser]);

  const isDataChanged = didObjectChange({
    initial: initialContactFormData,
    final: contactFormData,
    shouldDeepCompare: true,
  });

  const shouldShowConfirmationModal = isDataChanged && !job && !surveyType.includes('sample');

  const redirectToJobWithState = (jobKey: string) => {
    redirectToJob(jobKey);
  };

  const handleSendInviteError = (error: SendInviteError) => {
    const { isEmailError, isPhoneError, message } = error;
    dispatchState({ isSendInviteError: true, sendInviteError: message });
    setShouldShowTryAgainButton(!isEmailError && !isPhoneError);
  };

  const resendInvite = async () => {
    try {
      dispatchState({ isResendingInvite: true });

      const sendInviteParams: CreateInitialParamsData = {
        job,
        company: contactFormData.company,
        employee: contactFormData.employee,
        insured: contactFormData.insured,
        origin: contactFormData.origin,
        destination: contactFormData.destination,
      };

      const { identifier, jobKey } = await createInitialParams({
        data: sendInviteParams,
      });

      await sendSelfSurveyInvite({
        data: {
          ...sendInviteParams,
          identifier,
          jobKey,
          locale: jobFormState.locale,
          shouldSendEmail: contactFormData.shouldSendEmail,
          shouldSendSms: contactFormData.shouldSendSms,
        },
      });

      redirectToJobWithState(jobKey);
    } catch (error) {
      if (error instanceof SendInviteError) {
        handleSendInviteError(error);
        return;
      }

      notify.error('Failed to resend invite, please try again');
    } finally {
      dispatchState({ isResendingInvite: false });
    }
  };

  const createJob = async () => {
    const shouldSendInvite = shouldSendSms || shouldSendEmail;

    const type = surveyType === 'self_survey' && !shouldSendInvite ? 'manual_survey' : surveyType;

    if (contactFormData.origin.postalCode) {
      const updatedOriginPostalCode = contactFormData.origin.postalCode.replace(/[^A-Za-z0-9 ]/g, '');
      const isOriginCanada = contactFormData.origin.country === 'Canada' || contactFormData.origin.country === 'CA';
      // eslint-disable-next-line react-hooks/immutability
      contactFormData.origin.postalCode = isOriginCanada
        ? (formatCanadaPostalCode(updatedOriginPostalCode) ?? null)
        : updatedOriginPostalCode.trim();
    }
    if (contactFormData.destination.postalCode) {
      const updatedDestinationPostalCode = contactFormData.destination.postalCode.replace(/[^A-Za-z0-9 ]/g, '');
      const isDestinationCanada =
        contactFormData.destination.country === 'Canada' || contactFormData.destination.country === 'CA';

      contactFormData.destination.postalCode = isDestinationCanada
        ? (formatCanadaPostalCode(updatedDestinationPostalCode) ?? null)
        : updatedDestinationPostalCode.trim();
    }

    let job = null;

    if (type === 'self_survey') {
      const sendInviteParams: CreateInitialParamsData = {
        job: { ...jobFormState, status: 'invited', source: 'invite' },
        company: contactFormData.company,
        employee: contactFormData.employee,
        insured: contactFormData.insured,
        origin: contactFormData.origin,
        destination: contactFormData.destination,
      };

      const { identifier, jobKey } = await createInitialParams({
        data: sendInviteParams,
      });

      job = { key: jobKey };

      dispatchState({ job });

      await sendSelfSurveyInvite({
        data: {
          ...sendInviteParams,
          identifier,
          jobKey,
          locale: jobFormState.locale,
          shouldSendEmail: contactFormData.shouldSendEmail,
          shouldSendSms: contactFormData.shouldSendSms,
        },
      });
    } else {
      ({ job } = await createSurveyFlow({
        data: { ...contactFormData, job: jobFormState },
        type,
      }));

      dispatchState({ job });
    }

    const message = state.notes.trim();

    if (message) {
      const nowISO = new Date().toISOString();

      const notesActivity = {
        location: insured?.location ?? null,
        activityType: 'note',
        createdAt: nowISO,
        updatedAt: nowISO,
        message,
        jobKey: job.key,
        companyKey: contactFormData.company.key,
        employeeKey: authUser.key,
      };

      await postNoteApi({ data: notesActivity });
    }

    return job;
  };

  const updateFirstInviteSentAtTimestamp = async () => {
    if (!authUser.firstInviteSentAt && !surveyType.includes('sample')) {
      await putEmployee({
        key: authUser.key,
        firstInviteSentAt: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      });
    }
  };

  const runCreateFlow = async () => {
    if (!validation.isEmailValid) {
      dispatchContactFormData({ validation: { ...validation } });
      return;
    }

    try {
      dispatchState({ isAddingContact: true });

      const [customization] = await getCustomizations(selectedCompany.key);

      const { shouldBlockDuplicateMerging } = customization ?? {};

      if (!shouldBlockDuplicateMerging) {
        const e164Phone = toE164PhoneNumber({
          phone: contactFormData.insured.phone,
          regionCode: contactFormData.company.defaultCountry,
        });

        const result = await putInsuredCheckDuplicates({
          phone: e164Phone,
          email: contactFormData.insured.email ?? undefined,
          companyKey: contactFormData.company.key,
        });

        if (result.hasPotentialDuplicate) {
          const { jobKey, insured } = result;
          dispatchState({ job: { key: jobKey }, insured });
          return;
        }
      }

      await createJob();

      await updateFirstInviteSentAtTimestamp();

      if (surveyType.includes('sample')) closeAddContact();
    } catch (error) {
      if (error instanceof SendInviteError) {
        handleSendInviteError(error);
        return;
      }

      notify.error('Failed to add Contact, please try again');
    } finally {
      dispatchState({ isAddingContact: false });
    }
  };

  const redirectToJob = (key: string, state?: unknown) => {
    // biome-ignore lint/suspicious/noExplicitAny: Router type migration in progress
    navigate({ pathname: `/job/${key}/overview`, state } as any);
    closeAddContact();
  };

  const createNewSurvey = async () => {
    try {
      dispatchState({ isAddingNewContact: true });
      const { key: createdJobKey } = await createJob();
      await updateFirstInviteSentAtTimestamp();

      if (createdJobKey) redirectToJobWithState(createdJobKey);
    } catch (error) {
      if (error instanceof SendInviteError) {
        handleSendInviteError(error);
        return;
      }

      notify.error('Failed to add new survey, please try again');
    } finally {
      dispatchState({ isAddingNewContact: false });
    }
  };

  const shouldShowResendInviteError = job && isSendInviteError;
  const shouldShowSubmitMessage = (job && !isSendInviteError && !isAddingContact) || isAddingNewContact;
  const shouldShowDrawerBody = (!job && !isSendInviteError) || isAddingContact;

  return (
    <>
      <Drawer
        className={`add-contact-drawer ${shouldShowResendInviteError ? 'invite-error' : ''} ${shouldShowSubmitMessage ? 'contact-added' : ''} ${surveyType.includes('sample') ? 'sample-survey' : ''}`}
        headerText='Add Contact'
        onClose={shouldShowConfirmationModal ? () => setShowConfirmation(true) : closeAddContact}
        onAnimationEnd={(e) => {
          if (!e.animationName.includes('drawer')) return;

          handleAnimationEnd?.();
        }}
        buttonProps={
          job && !isAddingContact
            ? undefined
            : {
                isDisabled: !isFormValid,
                isLoading: isAddingContact,
                text: isInvite ? 'Add and Send Invite' : 'Add Contact',
                onClick: runCreateFlow,
              }
        }
      >
        {shouldShowResendInviteError && (
          <div className='resend-invite-error'>
            <div className='flex-center'>
              <ErrorIcon className='error-icon' />
            </div>
            <div className='inline flex-center error-message'>
              <p className='bold'>{sendInviteError ? 'Something went wrong' : 'Temporary system issue'}</p>
              <p>{sendInviteError || 'The invite wasn’t sent due to a technical hiccup. Please try again.'}</p>
            </div>
            <div className='column buttons flex-center'>
              {shouldShowTryAgainButton && (
                <Button text='Try Again' isLoading={isResendingInvite} onClick={resendInvite} />
              )}
              <Button
                isDisabled={isResendingInvite}
                onClick={() => redirectToJobWithState(job.key)}
                text='View Contact'
                kind='outline'
              />
            </div>
          </div>
        )}

        {shouldShowSubmitMessage && (
          <SubmitMessage
            isAddingContact={isAddingNewContact}
            isResendingInvite={isResendingInvite}
            isInvite={isInvite}
            insured={insured}
            onGoToJob={() => job && redirectToJobWithState(job.key)}
            // eslint-disable-next-line react-hooks/immutability
            onCreate={createNewSurvey}
            onBack={() => {
              dispatchState(initialContactState);
              dispatchContactFormData(initialContactFormData);
              dispatchJobFormState(initialJobState);
            }}
            resendInvite={resendInvite}
            surveyType={surveyType}
          />
        )}

        {shouldShowDrawerBody && (
          <DrawerBody
            state={state}
            jobState={jobFormState}
            formData={contactFormData}
            shouldIncludeSampleSurvey={activeRole === 'superadmin' || activeRole === 'admin'}
            dispatchState={dispatchState}
            dispatchJobState={dispatchJobFormState}
            dispatchFormData={dispatchContactFormData}
            setHandleAnimationEnd={setHandleAnimationEnd}
          />
        )}
      </Drawer>

      {showConfirmation && !job && (
        <Modal isDismissible={false}>
          <ModalHeader headerText='Close Contact Panel'>
            <p className='bold'> Are you sure you want to close this panel?</p>
          </ModalHeader>
          <ModalBody>You will lose the information you have entered.</ModalBody>
          <ModalFooter>
            <Button kind='outline' text='Back' onClick={() => setShowConfirmation(false)} />
            <Button
              isDanger
              text='Close'
              onClick={() => {
                setShowConfirmation(false);
                closeAddContact();
              }}
            />
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};
